'use client';

import { Image as ImageIcon } from 'lucide-react';
import Image from 'next/image';
import type { ComponentProps } from 'react';
import { cn } from '@/lib/utils';

interface ImagePreviewProperties {
  className?: string | undefined;
  imageUrl?: string | null | undefined;
  onError?: ComponentProps<typeof Image>['onError'];
  showFallback: boolean;
  title?: string | null | undefined;
}

export function ImagePreview({ imageUrl, title, onError, showFallback, className }: ImagePreviewProperties) {
  const shouldRenderImage = Boolean(imageUrl);

  if (!(shouldRenderImage || showFallback)) {
    return null;
  }

  if (shouldRenderImage) {
    return (
      <div
        className={cn('relative aspect-[2.4/1] overflow-hidden bg-gradient-to-br from-muted/50 to-muted', className)}
      >
        <Image
          src={imageUrl ?? ''}
          alt={title || 'Link preview'}
          fill
          className="object-cover transition-all duration-300 group-hover/link-preview:scale-105"
          style={{ borderRadius: 'inherit' }}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          onError={onError}
          priority={false}
          unoptimized
          data-link-preview-image
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover/link-preview:opacity-100 transition-all duration-200" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative h-24 bg-gradient-to-br from-muted/40 to-muted/20 flex items-center justify-center',
        className
      )}
      data-testid="image-preview-fallback"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/10 to-transparent opacity-50" />
      <ImageIcon className="w-12 h-12 text-muted-foreground/30" aria-hidden="true" />
    </div>
  );
}
